import { useOffsetPaginatedTable } from '../paginated-table/OffsetPaginatedTable'
import { TypeColumn } from '../paginated-table/AdminBaseTable'
import { useGodViewLogin } from '@/utils/adminHelpers'
import { Badge, Button, Loader } from '@/ui'
import { UserWithAdminInfo } from '@/reducers/admin/v2/allUsersReducerV2'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  getAllBookkeepersById,
  getIsAdministrator,
} from '@/selectors/user.selectors'
import { useReselector } from '@/utils/sharedHooks'
import {
  FETCH_ALL_ADMINS_KEY,
  fetchAllAdmins,
} from '@/actions/admin/adminAllUsersActions'
import { Alert } from '@/componentsV1/BaseComponents'
import { PrismaOffsetPagination } from '@/utils/fetchHelpers'
import {
  fetchPaginatedUsers,
  searchUsers,
  SearchType,
} from '@/actions/admin/v2/adminUsersActions'
import {
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAdminAllAnnualTaxDetailsIfNeeded,
} from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  ConfirmUserDeletion,
  FilterByBookkeeperIdDropdown,
  isUserIdSearch,
  SearchInput,
  SortByDropdown,
} from './controls'
import RowDrawer from './RowDrawer'
import { useAppDispatch } from '@/utils/typeHelpers'

const REQUIRED_SELECT_RELATIONS_FOR_TABLE = '&selectRelation=memberships'

const AdminListAllUsersTable = ({
  bookkeeperOptions,
}: {
  bookkeeperOptions: { key: number; text: string; value: number }[]
}) => {
  const hasAdministratorRole = useReselector(getIsAdministrator)

  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get('q')?.trim() ?? ''

  const [searchType, setSearchType] = useState<SearchType>(
    isUserIdSearch(search) ? SearchType.ById : SearchType.BySearchString
  )
  const [bookkeeperId, setBookkeeperId] = useState<number>()
  const [orderBy, setOrderBy] = useState<string | undefined>('createdAt:desc')

  const loginGodView = useGodViewLogin()

  const renderMembershipStatus = (user: UserWithAdminInfo) => {
    const primaryMembership = user.memberships?.find(
      (membership) => membership.isPrimary
    )
    const membershipStatus = primaryMembership?.status
    if (!membershipStatus) {
      return null
    }
    switch (membershipStatus) {
      case 'paid':
        return (
          <Badge variant="secondary" color="green">
            Paid
          </Badge>
        )
      case 'cancelling':
        return (
          <Badge variant="secondary" color="orange">
            Cancelling
          </Badge>
        )
      case 'canceled':
        return (
          <Badge variant="secondary" color="red">
            Canceled
          </Badge>
        )
      case 'unpaid':
        return (
          <Badge variant="secondary" color="red">
            Unpaid
          </Badge>
        )
      case 'trial':
        return (
          <Badge variant="secondary" color="yellow">
            Trial
          </Badge>
        )
      default:
        return (
          <Badge variant="secondary" color="gray">
            {membershipStatus}
          </Badge>
        )
    }
  }

  const cols: TypeColumn<UserWithAdminInfo>[] = [
    {
      header: 'ID',
      accessor: 'id',
      width: 1,
      cellProps: {
        collapsing: true,
      },
    },
    {
      header: 'Membership',
      accessor: 'memberships',
      width: 1,
      cellProps: {
        textAlign: 'center',
      },
      render: (user) => renderMembershipStatus(user),
    },
    { header: 'First Name', accessor: 'firstName', width: 2 },
    { header: 'Last Name', accessor: 'lastName', width: 2 },
    {
      header: 'Email',
      accessor: null,
      width: 2,
      render: (user) => {
        // wordwrap for email column to prevent overflow
        return <div style={{ wordWrap: 'break-word' }}>{user.email}</div>
      },
    },
    {
      header: 'Transactions',
      width: 1,
      accessor: null,
      render: (user) => (
        <Link to={`/admin/finances/transactions/${user.id}`}>Transactions</Link>
      ),
    },
    {
      header: 'User Record',
      width: 1,
      accessor: null,
      render: (user) => (
        <Link to={`/admin/finances/records/${user.id}`}>Details</Link>
      ),
    },
    {
      header: 'God View',
      accessor: null,
      width: 1,
      cellProps: {
        collapsing: true,
      },
      render: (user) => (
        <Button variant="danger" size="sm" onClick={() => loginGodView(user)}>
          God View
        </Button>
      ),
    },
  ]

  if (hasAdministratorRole) {
    cols.push({
      header: 'Delete User',
      accessor: null,
      width: 1,
      cellProps: {
        collapsing: true,
      },
      render: (user) => <ConfirmUserDeletion user={user} />,
    })
  }

  const fetchData = useCallback(
    (pagination: PrismaOffsetPagination) => {
      const orderByParam = orderBy ? `&orderBy=${orderBy}` : ''
      const additionalParams =
        REQUIRED_SELECT_RELATIONS_FOR_TABLE + orderByParam

      if (!search && !bookkeeperId) {
        return fetchPaginatedUsers(pagination, additionalParams)
      }
      return searchUsers(
        pagination as PrismaOffsetPagination,
        searchType,
        search,
        bookkeeperId,
        additionalParams
      )
    },
    // We don't want to trigger fetch when the searchType changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookkeeperId, search, orderBy]
  )

  const {
    renderTable,
    updateItem: updateUser,
    fetchError,
    resetPaginationAndActivePage,
  } = useOffsetPaginatedTable<UserWithAdminInfo>({
    columns: cols,
    fetchData,
    tableRowPropsConfig: {
      negative: (row) => row?.memberships?.[0]?.status === 'unpaid',
      renderDrawer: (row: UserWithAdminInfo) => (
        <RowDrawer
          row={row}
          colSpan={cols.length + 1}
          onUserUpdated={updateUser}
          bookkeeperOptions={bookkeeperOptions}
        />
      ),
    },
  })

  const onSearchSubmit = (searchString: string) => {
    if (searchString === '') {
      setSearchParams({})
      return
    }
    if (searchType === 'bySearchString' && searchString.length < 2) {
      alert('Search string must be at least 2 characters long')
      return
    } else if (searchType === SearchType.ById && isNaN(Number(searchString))) {
      alert('User id must be a number')
      return
    }
    setSearchParams({ q: searchString })
  }

  return (
    <div>
      {fetchError && <Alert type="error" title={fetchError.message} />}
      <div>
        <div className="flex gap-24" style={{ paddingBottom: 0 }}>
          <div>
            <h2>All Users</h2>
            <i>Excluding internal users</i>
          </div>
          <div className="flex flex-col items-end gap-4 grow-2">
            <SearchInput
              key={search}
              searchType={searchType}
              setSearchType={setSearchType}
              onSearchSubmit={onSearchSubmit}
              defaultValue={search}
            />
            <div style={{ display: 'flex', gap: 16, width: '100%' }}>
              <FilterByBookkeeperIdDropdown
                bookkeeperOptions={bookkeeperOptions}
                setBookkeeperId={setBookkeeperId}
                bookkeeperId={bookkeeperId}
                resetPaginationAndActivePage={resetPaginationAndActivePage}
              />
              <SortByDropdown
                orderBy={orderBy}
                setOrderBy={setOrderBy}
                resetPaginationAndActivePage={resetPaginationAndActivePage}
              />
            </div>
          </div>
        </div>
        <div style={{ paddingTop: 0 }}>{renderTable()}</div>
      </div>
    </div>
  )
}

const AdminListAllUsersPanel = () => {
  const dispatch = useAppDispatch()
  const isFetchingAdmins = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_ADMINS_KEY
  )
  const isFetchingTaxDetails = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY
  )
  const allBookkeepersById = useReselector(getAllBookkeepersById)
  const bookkeeperOptions = useMemo(
    () =>
      Object.values(allBookkeepersById).map((bookkeeper) => ({
        key: bookkeeper.id,
        text: `${bookkeeper.firstName} ${bookkeeper.lastName}`,
        value: bookkeeper.id,
      })),
    [allBookkeepersById]
  )

  useEffect(() => {
    dispatch(fetchAllAdmins())
    dispatch(fetchAdminAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  return isFetchingAdmins || isFetchingTaxDetails ? (
    <Loader active />
  ) : (
    <AdminListAllUsersTable bookkeeperOptions={bookkeeperOptions} />
  )
}

export default AdminListAllUsersPanel
